import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    IconButton,
    TextField,
    MenuItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Menu,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Schedule,
    ContentCopy,
    MoreVert,
    Search,
    FilterList,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';

const DraftsManager = () => {
    const navigate = useNavigate();
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPlatform, setFilterPlatform] = useState('');
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
    const [scheduledTime, setScheduledTime] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        fetchDrafts();
    }, [filterPlatform, searchTerm]);

    const fetchDrafts = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_BASE_URL}/api/drafts`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { platform: filterPlatform, search: searchTerm },
            });
            setDrafts(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching drafts:', error);
            setLoading(false);
        }
    };

    const handleCreateDraft = () => {
        navigate('/drafts/new');
    };

    const handleEditDraft = (draftId) => {
        navigate(`/drafts/edit/${draftId}`);
    };

    const handleDeleteDraft = async (draftId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذه المسودة؟')) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE_URL}/api/drafts/${draftId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchDrafts();
        } catch (error) {
            console.error('Error deleting draft:', error);
            alert('حدث خطأ أثناء حذف المسودة');
        }
    };

    const handleScheduleDraft = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE_URL}/api/drafts/${selectedDraft._id}/schedule`,
                { scheduledTime },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('✅ تم جدولة المنشور بنجاح!');
            setScheduleDialogOpen(false);
            setScheduledTime('');
            fetchDrafts();
        } catch (error) {
            console.error('Error scheduling draft:', error);
            alert('حدث خطأ أثناء جدولة المنشور');
        }
    };

    const handleDuplicateDraft = async (draftId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE_URL}/api/drafts/${draftId}/duplicate`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchDrafts();
        } catch (error) {
            console.error('Error duplicating draft:', error);
            alert('حدث خطأ أثناء نسخ المسودة');
        }
    };

    const getPlatformColor = (platform) => {
        const colors = {
            whatsapp: '#25D366',
            telegram: '#0088cc',
            instagram: '#E4405F',
            facebook: '#1877F2',
        };
        return colors[platform] || '#757575';
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">
                    المسودات
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleCreateDraft}
                >
                    مسودة جديدة
                </Button>
            </Box>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        placeholder="بحث في المسودات..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        select
                        label="تصفية حسب المنصة"
                        value={filterPlatform}
                        onChange={(e) => setFilterPlatform(e.target.value)}
                        InputProps={{
                            startAdornment: <FilterList sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                    >
                        <MenuItem value="">الكل</MenuItem>
                        <MenuItem value="whatsapp">WhatsApp</MenuItem>
                        <MenuItem value="telegram">Telegram</MenuItem>
                        <MenuItem value="instagram">Instagram</MenuItem>
                        <MenuItem value="facebook">Facebook</MenuItem>
                    </TextField>
                </Grid>
            </Grid>

            {/* Drafts Grid */}
            {drafts.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            لا توجد مسودات
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            ابدأ بإنشاء مسودة جديدة لحفظ أفكارك
                        </Typography>
                        <Button variant="contained" startIcon={<Add />} onClick={handleCreateDraft}>
                            إنشاء مسودة
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {drafts.map((draft) => (
                        <Grid item xs={12} md={6} lg={4} key={draft._id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                        <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
                                            {draft.title}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                setAnchorEl(e.currentTarget);
                                                setSelectedDraft(draft);
                                            }}
                                        >
                                            <MoreVert />
                                        </IconButton>
                                    </Box>

                                    <Chip
                                        label={draft.platform.toUpperCase()}
                                        size="small"
                                        sx={{
                                            bgcolor: getPlatformColor(draft.platform),
                                            color: 'white',
                                            mb: 2,
                                        }}
                                    />

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            mb: 2,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                        }}
                                    >
                                        {draft.content.text || 'لا يوجد نص'}
                                    </Typography>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            آخر تعديل: {formatDate(draft.lastEdited)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {draft.metadata.characterCount} حرف
                                        </Typography>
                                    </Box>
                                </CardContent>

                                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Edit />}
                                        onClick={() => handleEditDraft(draft._id)}
                                        fullWidth
                                    >
                                        تعديل
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<Schedule />}
                                        onClick={() => {
                                            setSelectedDraft(draft);
                                            setScheduleDialogOpen(true);
                                        }}
                                        fullWidth
                                    >
                                        جدولة
                                    </Button>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem
                    onClick={() => {
                        handleEditDraft(selectedDraft._id);
                        setAnchorEl(null);
                    }}
                >
                    <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
                    <ListItemText>تعديل</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleDuplicateDraft(selectedDraft._id);
                        setAnchorEl(null);
                    }}
                >
                    <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
                    <ListItemText>نسخ</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setScheduleDialogOpen(true);
                        setAnchorEl(null);
                    }}
                >
                    <ListItemIcon><Schedule fontSize="small" /></ListItemIcon>
                    <ListItemText>جدولة</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleDeleteDraft(selectedDraft._id);
                        setAnchorEl(null);
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>حذف</ListItemText>
                </MenuItem>
            </Menu>

            {/* Schedule Dialog */}
            <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)}>
                <DialogTitle>جدولة المنشور</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        type="datetime-local"
                        label="وقت النشر"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        sx={{ mt: 2 }}
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScheduleDialogOpen(false)}>إلغاء</Button>
                    <Button onClick={handleScheduleDraft} variant="contained">
                        جدولة
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default DraftsManager;
